// setUploadJobStatus.js
'use strict';
import {
  REMOVE_LIST_ITEMS,
  REMOVE_OPERATION_DATA,
  MERGE_OPERATION_DATA,
} from '../ActionTypes.js';

import processUploadJob from '../action/processUploadJob.js';
import fetchFeeds from '../action/fetchFeeds.js';

import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getMeData from '../selector/getMeData.js';
import getUploadJobIds from '../selector/getUploadJobIds.js';
import getFeedName from '../selector/getFeedName.js';
import { getSelectedQueryOptionObject } from '../selector/getFeedQueryObject.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

import {
  Status as UploadStatus,
  Entry as UploadJobType,
} from '../resource/uploadConstants.js';
import { upload as uploadDebug } from '../resource/debug.js';
import {
  PROFILE_FEED_REMOTE_CONFIG_KEY_MAP,
  ProfileListMediaType,
  ProfileListType,
} from '../resource/profileConstants.js';
import { QueryPrefix, QueryType } from '../resource/feedConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

const uploadLog = uploadDebug.extend('log:action/setUploadJobStatus.js');

/**
 * Set upload status
 * @kind action
 * @param {string} {uploadJobId} - upload job id.
 * @param {string} {status} - upload status.
 * @param {string} {failedReason} - upload failed reason.
 * @return {Promise} Action promise.
 */
const setUploadJobStatus =
  ({ uploadJobId, status, failedReason }) =>
  async dispatch => {
    uploadLog('execute setUploadJobStatus', {
      uploadJobId,
      status,
      failedReason,
    });
    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['uploadJob', uploadJobId],
        data: {
          status,
          reason: failedReason,
        },
      },
    });
    switch (status) {
      case UploadStatus.UPLOAD_FAILED:
      case UploadStatus.UPLOAD_COMPLETED: {
        return dispatch(processUploadJob());
      }
      case UploadStatus.DELIVERY_COMPLETED: {
        dispatch(refetchProfileList({ uploadJobId, status }));
        dispatch({
          type: REMOVE_LIST_ITEMS,
          payload: {
            selectPath: ['uploadJob'],
            itemIds: [uploadJobId],
          },
        });
        return dispatch({
          type: REMOVE_OPERATION_DATA,
          payload: {
            selectPath: ['uploadJob', uploadJobId],
          },
        });
      }
    }
  };

/**
 * Refetch profile list.
 * @kind action
 * @param {string} {uploadJobId} - upload job id.
 * @param {string} {status} - upload status.
 * @return {Promise} Action promise.
 */
const refetchProfileList =
  ({ uploadJobId, status } = {}) =>
  async (dispatch, getState) => {
    uploadLog('execute refetchProfileList', {
      uploadJobId,
      status,
    });
    const messageId = getOperationData(
      getState(),
      ['uploadJob', uploadJobId],
      'messageId'
    );
    if (!messageId) {
      uploadLog('early return if message id not exist');
      return dispatch({ type: '' });
    }
    const listType = [
      {
        uploadJobType: UploadJobType.STORY,
        mediaType: '*',
        listType: ProfileListType.STORY,
      },
      {
        uploadJobType: UploadJobType.POST,
        mediaType: `${ProfileListMediaType.VIDEO}`,
        listType: ProfileListType.FLIX,
      },
      {
        uploadJobType: UploadJobType.POST,
        mediaType: `${ProfileListMediaType.IMAGE}`,
        listType: ProfileListType.IMAGE,
      },
      {
        uploadJobType: UploadJobType.CLIP,
        mediaType: '*',
        listType: ProfileListType.SHORT,
      },
    ].find(({ uploadJobType, mediaType }) => {
      const jobIds =
        getUploadJobIds(getState(), uploadJobType, mediaType) || [];
      return jobIds.includes(uploadJobId);
    })?.listType;
    if (!listType) {
      uploadLog('early return if list type is unknown', {
        uploadJobId,
        uploadJob: getOperationData(getState(), ['uploadJob']),
      });
      return dispatch({ type: '' });
    }
    const feedNameRemoteConfigKey =
      PROFILE_FEED_REMOTE_CONFIG_KEY_MAP[listType];
    const outboxFeedName = getFeedName(
      getState(),
      feedNameRemoteConfigKey,
      '{USER_ID}',
      getMeData(getState(), 'id')
    );
    const { feedNameWithQuery } = getSelectedQueryOptionObject(
      getState(),
      outboxFeedName,
      '',
      getRemoteConfigData(
        getState(),
        `${feedNameRemoteConfigKey}_${QueryPrefix[QueryType.SORT]}`
      )?.[0]
    );
    const nowUnix = getCurrentUnixTimestamp({
      offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
    });
    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['profileFeed'],
        data: {
          unixTimestamp: nowUnix,
        },
      },
    });
    return dispatch(
      fetchFeeds({
        type: feedNameWithQuery,
        page: 1,
        unixTimestamp: nowUnix,
        shouldSkipLocalCache: true,
      })
    );
  };

export default setUploadJobStatus;
